<template>
    <div class="animated fadeIn">
        <b-card>
            <b-card-header>
                <i class="icon-puzzle mr-1"></i> Integrations
            </b-card-header>
            <b-card-body>
                <b-alert variant="info" show>
                    Connect third-party services for your business!
                </b-alert>

                <!-- b-alert v-if="!businessId" variant="warning" show>
                    Select a business to configure integrations.
                </b-alert -->

                <div class="d-flex flex-wrap gap-3 mb-4">
                    <span v-for="(config, index) in serviceConfigs" :key="config.service" class="d-inline-block p-2"
                        :id="'tooltip-' + index">
                        <b-button :variant="selectedService === config.service ? 'primary' : 'outline-primary'"
                            @click="selectedService = config.service">
                            <i :class="config.icon + ' mr-1'"></i>
                            {{ config.label }}
                        </b-button>
                    </span>
                    <b-tooltip v-for="(config, index) in serviceConfigs" :key="'tooltip-' + index"
                        :target="'tooltip-' + index">
                        <!-- template
                            v-if="config.accessLevel === 'admin' && !isAdmin && !integrations[config.service]?.id">
                            Admin access required to initiate configuration of<br /><b>{{ config.label }}</b>
                        </template -->
                        <template v-if="config.implemented">
                            Click here to configure<br /><b>{{ config.label }}</b>
                        </template>
                        <template v-else>
                            {{ config.label }}<br />integration coming soon!
                        </template>
                    </b-tooltip>
                </div>

                <b-form @submit.prevent="onSubmit" @reset.prevent="onReset" autocomplete="off">
                    <template v-if="selectedService && selectedConfig">
                        <b-alert v-if="!selectedConfig.implemented" variant="secondary" show>
                            {{ selectedConfig.label }} support is coming soon.
                        </b-alert>

                        <b-card class="mb-3" no-body>
                            <b-card-header class="d-flex justify-content-between align-items-center">
                                <span>
                                    <i :class="selectedConfig.icon + ' mr-1'"></i>
                                    {{ selectedConfig.label }}
                                </span>
                                <span>
                                    <b-badge v-if="selectedIntegration.id && selectedForm.enabled"
                                        variant="success">Connected</b-badge>
                                    <b-badge v-else-if="selectedIntegration.id" variant="secondary">Saved
                                        (Disabled)</b-badge>
                                    <b-badge v-else variant="light">Not Configured</b-badge>
                                </span>
                            </b-card-header>

                            <b-card-body>
                                <b-form-checkbox :id="selectedService + '-enabled'" v-model="selectedForm.enabled"
                                    class="mb-3" :disabled="!selectedConfig.implemented">
                                    Enable {{ selectedConfig.label }}
                                </b-form-checkbox>

                                <b-form-group v-if="selectedConfig && selectedConfig.authType === 'oauth2'"
                                    label="Client ID" :label-for="selectedService + '-client-id'"
                                    description="From your provider developer application settings.">
                                    <b-form-input :id="selectedService + '-client-id'"
                                        v-model.trim="selectedForm.clientId" required placeholder="Enter client_id"
                                        :disabled="!selectedConfig.implemented" autocomplete="off"></b-form-input>
                                </b-form-group>

                                <b-form-group v-if="selectedConfig && selectedConfig.authType === 'oauth2'"
                                    label="Client Secret" :label-for="selectedService + '-client-secret'"
                                    description="Stored as provided and used for token exchange.">
                                    <b-form-input :id="selectedService + '-client-secret'"
                                        v-model.trim="selectedForm.clientSecret" :required="!selectedIntegration.id"
                                        type="password"
                                        placeholder="Enter client secret (only required for new configurations)"
                                        :disabled="!selectedConfig.implemented" autocomplete="off"></b-form-input>
                                </b-form-group>
                            </b-card-body>
                        </b-card>

                        <div class="clearfix">
                            <b-button type="button" variant="warning" @click="onCancel" class="mr-2">Cancel</b-button>
                            <b-button type="reset" variant="danger"
                                :disabled="loading || saving || !selectedConfig.implemented">Reset</b-button>
                            <b-button type="submit" variant="success" class="float-right"
                                :disabled="loading || saving || !selectedConfig.implemented">
                                {{ saving ? 'Saving...' : 'Save ' + selectedConfig.label }}
                            </b-button>
                        </div>
                    </template>
                </b-form>
            </b-card-body>
        </b-card>
        <template v-if="selectedIntegration?.encodedAccountId && showProviderComponent">
            <component :is="providerComponent" :provider="selectedService"></component>
        </template>
    </div>
</template>

<script>
import { HTTP } from '@/services/http-common'
import SERVICE_CONFIGS from './provider-configs'
import business from '@/store/modules/business'

const defaultForm = () => ({
    enabled: false,
    clientId: '',
    clientSecret: ''
})

const initFormsByService = () => {
    return SERVICE_CONFIGS.reduce((acc, config) => {
        acc[config.service] = defaultForm()
        return acc
    }, {})
}

const initIntegrationsByService = () => {
    return SERVICE_CONFIGS.reduce((acc, config) => {
        acc[config.service] = { id: null }
        return acc
    }, {})
}

export default {
    name: 'Integrations',
    props: {
        bid: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            serviceConfigs: SERVICE_CONFIGS,
            selectedService: null,
            loading: false,
            saving: false,
            integrations: initIntegrationsByService(),
            forms: initFormsByService(),
            initialForms: initFormsByService(),
            providerComponent: null
        }
    },
    computed: {
        businessId() {
            return this.bid || (this.currentBusiness ? this.currentBusiness.id : null)
        },
        selectedConfig() {
            return this.serviceConfigs.find(config => config.service === this.selectedService) || this.serviceConfigs[0]
        },
        selectedIntegration() {
            return this.integrations[this.selectedService]
        },
        selectedForm() {
            return this.forms[this.selectedService]
        },
        serviceSelectOptions() {
            return this.serviceConfigs.map(config => {
                // @TODO - allow acess after initial connection by admin so directors can select thier primary list
                if (config.accessLevel === 'admin' && !this.isAdmin) {
                    return {
                        text: config.label + ' (Admin Access Required)',
                        value: null,
                        disabled: true
                    }
                }
                return {
                    text: config.implemented ? config.label : config.label + ' (Coming Soon)',
                    value: config.service
                }
            })
        },
        showProviderComponent() {
            let addtlChecks = true
            if (this.selectedService?.service == 'constant_contact_partner') {
                addtlChecks = this.integrations[this.selectedService]?.encodedAccountId != null
            }
            return this.selectedService &&
                this.selectedConfig.implemented &&
                this.integrations[this.selectedService] &&
                this.integrations[this.selectedService].id &&
                addtlChecks
        },
    },
    mounted() {
        this.loadIntegrations()
    },
    watch: {
        businessId: {
            immediate: true,
            handler() {
                this.loadIntegrations()
            }
        },
        selectedService() {
            // Dynamically import provider-specific component if it exists
            // const providerComponentName = this.selectedConfig.service + '-integration-provider'
            switch (this.selectedConfig.service) {
                case 'constant_contact_partner':
                case 'constant_contact':
                case 'active_campaign':
                    this.providerComponent = () => import('./providers/~EmailMarketingProvider.vue')
                    break
                default:
                    this.providerComponent = null
            }
        },
        // check for status query string 
        '$route.query.status': {
            immediate: true,
            handler(newValue) {
                if (newValue === 'success') {
                    this.$bvToast.toast('Integration connected successfully!', {
                        title: 'Success',
                        variant: 'success',
                        autoHideDelay: 5000,
                        appendToast: false
                    })
                } else if (newValue === 'failure') {
                    this.$bvToast.toast('There was an error connecting the integration.', {
                        title: 'Connection Error',
                        variant: 'danger',
                        autoHideDelay: 5000,
                        appendToast: false
                    })
                }
            }
        },
        // check for service query string to auto-select service after redirect from provider connection flow
        '$route.query.service': {
            immediate: true,
            handler(newValue) {
                if (newValue && this.serviceConfigs.some(config => config.service === newValue)) {
                    this.selectedService = newValue
                }
            }
        }
    },
    methods: {
        businessIri() {
            return '/api/businesses/' + this.businessId
        },
        hydrateForm(service, record) {
            this.$set(this.integrations, service, {
                id: record && record.id ? record.id : null,
                encodedAccountId: record && record.encodedAccountId ? record.encodedAccountId : null
            })

            const form = {
                enabled: !!(record && record.enabled),
                clientId: record && record.clientId ? record.clientId : '',
                clientSecret: record && record.clientSecret ? record.clientSecret : ''
            }

            this.$set(this.forms, service, form)
            this.$set(this.initialForms, service, { ...form })
        },
        async loadIntegrations() {
            const implementedServices = this.serviceConfigs.filter(config => config.implemented)

            if (!this.businessId) {
                implementedServices.forEach(config => {
                    this.hydrateForm(config.service, null)
                })
                return
            }

            this.loading = true
            const failedServices = []

            try {
                for (let i = 0; i < implementedServices.length; i++) {
                    const config = implementedServices[i]
                    try {
                        const response = await HTTP.get('/service_apis', {
                            params: {
                                business: this.businessId,
                                service: config.service
                            }
                        })

                        const records = response.data && response.data['hydra:member']
                            ? response.data['hydra:member']
                            : []

                        this.hydrateForm(config.service, records.length ? records[0] : null)
                    } catch (error) {
                        failedServices.push(config.label)
                        this.hydrateForm(config.service, null)
                    }
                }

                if (failedServices.length) {
                    this.$bvToast.toast('Unable to load: ' + failedServices.join(', '), {
                        title: 'Integration Error',
                        variant: 'warning',
                        autoHideDelay: 5000,
                        appendToast: false
                    })
                }
            } catch (error) {
                this.$bvToast.toast('Unable to load integrations at this time.', {
                    title: 'Integration Error',
                    variant: 'warning',
                    autoHideDelay: 5000,
                    appendToast: false
                })
            } finally {
                this.loading = false
            }
        },
        onReset() {
            this.$set(this.forms, this.selectedService, { ...this.initialForms[this.selectedService] })
        },
        async onSubmit() {
            if (!this.businessId || !this.selectedConfig.implemented) {
                return
            }

            this.saving = true
            const basicAuthValue = 'partner'
            const service = this.selectedService
            const integration = this.integrations[service]
            const form = this.forms[service]

            const payload = {
                business: this.businessId ? '/api/businesses/' + this.businessId : null,
                service: service,
                enabled: !!form.enabled,
                clientId: this.selectedConfig.authType === 'oauth2' ? form.clientId : basicAuthValue,
                // clientSecret: form.clientSecret only include if it's set in the form, otherwise we may overwrite an existing secret with an empty value
                ...(form.clientSecret ? { clientSecret: form.clientSecret } : this.selectedConfig.authType === 'oauth2' ? {} : { clientSecret: basicAuthValue })
            }

            try {
                let response
                if (integration.id) {
                    response = await HTTP.put('/service_apis/' + integration.id, payload)
                } else {
                    response = await HTTP.post('/service_apis', payload)
                }

                this.hydrateForm(service, response.data)
                try {
                    switch (service) {
                        case 'constant_contact':
                        case 'active_campaign':
                            await this.initiateConnectionFlow(service)
                            break
                        case 'constant_contact_partner':
                            await this.enablePartnerAcct()
                            break
                        default:
                            // no connection flow for other services at this time
                            break
                    }
                    this.$bvToast.toast('Integration saved successfully.', {
                        title: 'Success',
                        variant: 'success',
                        autoHideDelay: 5000,
                        appendToast: false
                    })
                } catch (error) {
                    // this.$bvToast.toast('Integration enabled, but unable to initiate connection flow.', {
                    //     title: 'Partial Success',
                    //     variant: 'warning',
                    //     autoHideDelay: 5000,
                    //     appendToast: false
                    // })
                }
            } catch (error) {
                this.$bvToast.toast('Unable to save integration settings.', {
                    title: 'Save Failed',
                    variant: 'warning',
                    autoHideDelay: 5000,
                    appendToast: false
                })
            } finally {
                this.saving = false
            }
        },
        async initiateConnectionFlow(service) {
            const connect = await HTTP.get(`/businesses/${this.businessId}/integrations/${service}/connect`)
            // redirect to the URL provided in the response to complete the connection flow
            if (connect.data && connect.data.url) {
                window.location.href = connect.data.url
            } else {
                throw new Error('No connection URL provided')
            }
        },
        async enablePartnerAcct() {
            return await HTTP.post(`/businesses/${this.businessId}/integrations/constant_contact_partner/enable`, {})
        },
        onCancel() {
            this.selectedService = null
        }
    }
}
</script>
